import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WordCarousel from '../components/WordCarousel';
import AtomOrbit from '../components/AtomOrbit';

// ── Hex grid background for hero ─────────────────────────────────────────────
const SQ3 = Math.sqrt(3);
const HEX_R = 46;               // circumradius (center → vertex)
const COL_W = SQ3 * HEX_R;     // pointy-top: center-to-center horizontal
const ROW_H = 1.5 * HEX_R;     // pointy-top: center-to-center vertical

// Colour tiers for blue accent hexes — kept deliberately subtle
const HEX_STYLES = {
  bright: { fill: 'rgba(59,130,246,0.09)', stroke: 'rgba(59,130,246,0.28)' },
  medium: { fill: 'rgba(59,130,246,0.05)', stroke: 'rgba(59,130,246,0.16)' },
  faint:  { fill: 'rgba(59,130,246,0.02)', stroke: 'rgba(59,130,246,0.09)' },
  dark:   { fill: 'transparent',            stroke: 'rgba(255,255,255,0.032)' },
};

// [col, row] → tier   (cols 8-18 = right-hand half of a 1440-wide viewport)
const BLUE_HEXES = new Map([
  // Far-right bright anchors
  ['14,2',  'bright'], ['16,6',  'bright'], ['13,9',  'bright'],
  // Mid-right mediums
  ['15,4',  'medium'], ['17,2',  'medium'], ['12,5',  'medium'],
  ['13,11', 'medium'], ['16,9',  'medium'], ['18,5',  'medium'],
  // Mid-right faints
  ['11,3',  'faint'],  ['10,7',  'faint'],  ['12,1',  'faint'],
  ['15,11', 'faint'],  ['17,8',  'faint'],  ['19,4',  'faint'],
  ['11,9',  'faint'],
  // New — populates the 38–54% zone that the wider mask now exposes
  ['9,4',   'faint'],  ['8,7',   'faint'],  ['10,1',  'faint'],
  ['9,10',  'faint'],  ['8,2',   'faint'],
  ['11,7',  'medium'], ['10,5',  'medium'],
]);

function HeroHexGrid() {
  const COLS = 21;
  const ROWS = 14;
  const polygons = [];

  for (let col = 0; col < COLS; col++) {
    for (let row = 0; row < ROWS; row++) {
      // Odd rows shift right by half a column width
      const cx = col * COL_W + (row % 2 === 1 ? COL_W / 2 : 0) + COL_W / 2;
      const cy = row * ROW_H + HEX_R;
      const pts = Array.from({ length: 6 }, (_, k) => {
        const a = (30 + 60 * k) * Math.PI / 180; // pointy-top orientation
        return `${(cx + HEX_R * Math.cos(a)).toFixed(2)},${(cy + HEX_R * Math.sin(a)).toFixed(2)}`;
      }).join(' ');
      const key = `${col},${row}`;
      const tier = BLUE_HEXES.get(key) || 'dark';
      polygons.push({ pts, key, ...HEX_STYLES[tier] });
    }
  }

  const vw = COLS * COL_W + COL_W;
  const vh = ROWS * ROW_H + HEX_R * 2;

  return (
    <svg
      viewBox={`0 0 ${vw.toFixed(0)} ${vh.toFixed(0)}`}
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
      className="absolute inset-0 w-full h-full"
      style={{
        WebkitMaskImage: 'linear-gradient(to right, transparent 0%, transparent 36%, rgba(0,0,0,0.25) 46%, rgba(0,0,0,0.68) 55%, black 64%)',
        maskImage:       'linear-gradient(to right, transparent 0%, transparent 36%, rgba(0,0,0,0.25) 46%, rgba(0,0,0,0.68) 55%, black 64%)',
      }}
    >
      {polygons.map(({ pts, key, fill, stroke }) => (
        <polygon key={key} points={pts} fill={fill} stroke={stroke} strokeWidth="1" />
      ))}
    </svg>
  );
}

const Home = () => {
  return (
    <div className="bg-[#060A0F] text-[#F8FAFC] font-inter min-h-screen">
      <Helmet>
        <title>Nordic Numbers | Leaders Who Know Why Win | Causal AI</title>
        <meta name="description" content="Stop making decisions based on correlation. Nordic Numbers gives executives, investors, and institutions the causal intelligence to understand why outcomes happen, and how to engineer the ones you want." />
        <meta name="keywords" content="causal AI, causal analysis, causal data analytics, causal reasoning, causal machine learning, decision intelligence, competitive advantage, structural advantage, evidence-based strategy, hypothesis-driven strategy, investment analytics, causal factor investing, marketing attribution, sovereign wealth fund analytics, C-suite analytics, executive decision making, data analytics Norway, causal analysis Bergen, SKAI Epistemology, Five Rings Methodology, next-best-action, cause and effect business" />
        <link rel="canonical" href="https://www.nordicnumbers.no/" />
        <meta property="og:title" content="Nordic Numbers | Leaders Who Know Why Win | Causal AI" />
        <meta property="og:description" content="Stop making decisions based on correlation. Nordic Numbers gives executives, investors, and institutions the causal intelligence to understand why outcomes happen — and how to engineer the ones you want." />
        <meta property="og:image" content="https://www.nordicnumbers.no/bigger_logo.png" />
        <meta property="og:url" content="https://www.nordicnumbers.no" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="Nordic Numbers | Leaders Who Know Why Win | Causal AI" />
        <meta name="twitter:description" content="Stop making decisions based on correlation. Nordic Numbers gives executives, investors, and institutions the causal intelligence to understand why outcomes happen — and how to engineer the ones you want." />
        <meta name="twitter:image" content="https://www.nordicnumbers.no/bigger_logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{`{"@context":"https://schema.org","@type":"FAQPage","mainEntity":[{"@type":"Question","name":"What is causal AI and why does it outperform traditional machine learning?","acceptedAnswer":{"@type":"Answer","text":"Causal AI identifies the actual mechanisms that produce outcomes, not just statistical associations between variables. Traditional machine learning describes correlations that can be spurious and collapse when the environment changes. Causal AI encodes how a system works, generalises across contexts, and enables confident intervention. Nordic Numbers deploys SKAI, Epistemology's causal AI platform, to deliver this rigour to business and institutional strategy."}},{"@type":"Question","name":"What competitive advantage does causal analysis provide to executives and investors?","acceptedAnswer":{"@type":"Answer","text":"When decisions are grounded in causal mechanisms rather than correlations, the resulting strategy cannot be reverse-engineered from observable market behaviour. Executives gain the ability to engineer outcomes rather than merely react to them. Investors identify structural causes of return rather than chasing historical patterns. Nordic Numbers creates this durable structural advantage for clients across investment, governance, defence, and marketing."}},{"@type":"Question","name":"Which industries does Nordic Numbers serve with causal AI?","acceptedAnswer":{"@type":"Answer","text":"Nordic Numbers applies causal analytics across investment management, digital governance and AI policy, defence and security, and marketing attribution. The methodology, powered by the Five Rings framework developed by Epistemology, generalises to any domain where understanding causation is more valuable than describing correlation."}},{"@type":"Question","name":"How does causal analysis help with difficult business decisions under uncertainty?","acceptedAnswer":{"@type":"Answer","text":"Causal models encode the mechanisms behind complex systems and operate effectively in data-sparse environments. They enable agent intent modelling and next-best-action calculations even when historical data is limited. This means executives and institutions can make confident decisions without needing large datasets or ideal conditions."}}]}`}</script>
      </Helmet>

      <Navbar />

      {/* ── Section 1: Hero ─────────────────────────────────────── */}
      <section className="relative w-full h-screen min-h-[600px] flex items-center overflow-hidden bg-[#060A0F]">

        {/* Hex grid — fades in from the right, invisible behind the text */}
        <HeroHexGrid />

        {/* Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-12">

          {/* Left — heading + CTA */}
          <div className="flex-1 max-w-xl">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-px bg-blue-500/60" />
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-blue-400">
                Strategic Causal AI
              </p>
            </div>
            <h1
              className="font-extrabold leading-[1.08]"
              style={{ fontSize: 'clamp(2.9rem, 5vw, 4rem)' }}
            >
              <span className="block md:inline gradient-text">We help you </span><span className="gradient-text-blue">understand</span>
            </h1>
            <p className="mt-6 text-slate-400 leading-[1.75]" style={{ fontSize: 'clamp(1.05rem, 1.5vw, 1.1rem)' }}>
              Leaders who shape outcomes understand the causes behind results and act accordingly.
              <span className="text-slate-300"> This clarity drives consistent results, and builds a competitive advantage your rivals cannot replicate.</span>
            </p>
            <div className="mt-8">
              <Link
                to="/contact"
                className="relative inline-flex items-center gap-3 px-8 py-3.5 rounded-full border border-blue-500/60 text-white font-semibold text-sm tracking-wide group overflow-hidden transition-all duration-500 hover:border-blue-400 hover:shadow-[0_0_36px_rgba(59,130,246,0.28)]"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="relative">Get in touch</span>
                <svg className="relative w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </div>

          {/* Right — word carousel (hidden on mobile, shown md+) */}
          <div className="hidden md:flex flex-1 justify-end">
            <WordCarousel />
          </div>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center animate-bounce opacity-40 hover:opacity-70 transition-opacity duration-300">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
            <path d="M12 5v14M5 12l7 7 7-7" />
          </svg>
        </div>
      </section>
      {/* section divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-blue-500/[0.22] to-transparent" />

      {/* ── Section 2: Sectors ──────────────────────────────────── */}
      <section id="expertise" className="py-24 md:py-32 px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-8 h-px bg-blue-500/60" />
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-blue-400">Our Expertise</p>
              <div className="w-8 h-px bg-blue-500/60" />
            </div>
            <h2
              className="font-extrabold text-white leading-[1.1]"
              style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)' }}
            >
              One problem. Different industries.
            </h2>
            <p className="mt-4 text-slate-500 max-w-xl mx-auto" style={{ fontSize: 'clamp(0.9rem, 1.4vw, 1rem)' }}>
              The same fundamental gap — a lack of causal understanding — shows up across every sector we work with.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
            {[
              {
                icon: (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
                  </svg>
                ),
                iconBg: 'bg-blue-500/[0.12]',
                iconCls: 'text-blue-400',
                category: 'Investment Strategy',
                labelCls: 'text-blue-400/70',
                headline: 'Which decisions drive alpha?',
                body: 'Understanding the causal drivers behind portfolio returns enables consistent outperformance, honest uncertainty quantifiaction, and positions you can defend to any stakeholder.',
                keywords: ['Alpha Generation', 'Causal Factors', 'Risk Decomposition'],
                tagBg: 'bg-blue-500/[0.1]',
                tagCls: 'text-blue-300',
                hoverCls: 'hover:border-blue-500/40 hover:shadow-[0_0_44px_rgba(59,130,246,0.09)]',
              },
              {
                icon: (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                  </svg>
                ),
                iconBg: 'bg-indigo-500/[0.12]',
                iconCls: 'text-indigo-400',
                category: 'Digital Governance',
                labelCls: 'text-indigo-400/70',
                headline: 'Which policies create lasting change?',
                body: 'Digital governance operates inside complex, fast-moving systems, with an increasing need for explainable decisions. We design technically rigorous and ethically coherent governance frameworks.',
                keywords: ['Policy Impact', 'Regulatory AI', 'Intervention Mapping'],
                tagBg: 'bg-indigo-500/[0.1]',
                tagCls: 'text-indigo-300',
                hoverCls: 'hover:border-indigo-500/40 hover:shadow-[0_0_44px_rgba(99,102,241,0.09)]',
              },
              {
                icon: (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                  </svg>
                ),
                iconBg: 'bg-emerald-500/[0.12]',
                iconCls: 'text-emerald-400',
                category: 'Climate Science',
                labelCls: 'text-emerald-400/70',
                headline: 'Which actions move the needle?',
                body: 'Climate and environmental systems are deeply interconnected, requiring causal models able to handle feedback loops and recommend valuable intervetions in the face of uncertainty.',
                keywords: ['Emissions Tracing', 'Tipping Points', 'Carbon Attribution'],
                tagBg: 'bg-emerald-500/[0.1]',
                tagCls: 'text-emerald-300',
                hoverCls: 'hover:border-emerald-500/40 hover:shadow-[0_0_44px_rgba(16,185,129,0.09)]',
              },
              {
                icon: (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                  </svg>
                ),
                iconBg: 'bg-violet-500/[0.12]',
                iconCls: 'text-violet-400',
                category: 'Space Awareness',
                labelCls: 'text-violet-400/70',
                headline: 'What caused that, and who intended it?',
                body: 'From orbital debris to agent intent modelling, our methods identifies plausible origins and trajectory of future events in contested orbital environments, enabling collision mitiagtion strategies.',
                keywords: ['Debris Attribution', 'Intent Modelling', 'Orbital Forensics'],
                tagBg: 'bg-violet-500/[0.1]',
                tagCls: 'text-violet-300',
                hoverCls: 'hover:border-violet-500/40 hover:shadow-[0_0_44px_rgba(139,92,246,0.09)]',
              },
            ].map(({ icon, iconBg, iconCls, category, labelCls, headline, body, keywords, tagBg, tagCls, hoverCls }, i) => (
              <div
                key={i}
                className={`group relative flex flex-col border border-white/[0.08] rounded-2xl p-7 bg-white/[0.01] transition-all duration-500 ${hoverCls}`}
              >
                {/* Icon */}
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-6 ${iconBg} ${iconCls} transition-opacity duration-300 opacity-80 group-hover:opacity-100`}>
                  {icon}
                </div>

                {/* Text */}
                <div className="flex-1">
                  <p className={`text-[0.68rem] font-bold uppercase tracking-[0.25em] ${labelCls} mb-2`}>{category}</p>
                  <h3 className="text-white font-bold leading-snug mb-3" style={{ fontSize: 'clamp(1rem, 1.5vw, 1.1rem)' }}>
                    {headline}
                  </h3>
                  <p className="text-slate-500 leading-[1.7]" style={{ fontSize: 'clamp(0.85rem, 1.2vw, 0.9rem)' }}>
                    {body}
                  </p>
                </div>

                {/* Keyword tags */}
                <div className="mt-6 pt-5 border-t border-white/[0.06] flex flex-wrap gap-2">
                  {keywords.map((kw) => (
                    <span key={kw} className={`text-[0.7rem] font-semibold px-2.5 py-1 rounded-md ${tagBg} ${tagCls} tracking-wide`}>
                      {kw}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* section divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-blue-500/[0.22] to-transparent" />

      {/* ── Section 3: The Problem ───────────────────────────────── */}
      <section className="relative py-24 md:py-36 px-6 md:px-10 overflow-hidden">
        <div className="relative max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">

            {/* Left — text */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-10 h-px bg-blue-500/60 flex-shrink-0" />
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-blue-400">The universal challenge</p>
              </div>
              <h2
                className="font-extrabold leading-[1.08] mb-8"
                style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)' }}
              >
                <span className="text-white">Every leader asks:</span>
                <br />
                <span className="gradient-text-blue">which levers do I pull?</span>
              </h2>
              <div className="space-y-5">
                <p className="text-slate-400 leading-[1.8]" style={{ fontSize: 'clamp(1rem, 1.5vw, 1.075rem)' }}>
                  You can see your numbers. You can track your metrics. But knowing{' '}
                  <strong className="text-slate-200">which actions cause which outcomes</strong> — and how
                  reliably — is the gap between organizations that consistently hit their targets and those
                  that are perpetually surprised by results.
                </p>
                <p className="text-slate-500 leading-[1.8] pt-3 border-t border-white/[0.06]" style={{ fontSize: 'clamp(0.9rem, 1.4vw, 1rem)' }}>
                  Correlation tells you what moves together.{' '}
                  <span className="text-blue-400/80">Causation tells you what to do about it.</span>
                </p>
              </div>
            </div>

            {/* Right — YouTube embed */}
            <div className="relative flex-shrink-0 w-full lg:w-[52%]">
              <div className="relative rounded-2xl overflow-hidden shadow-[0_0_60px_rgba(59,130,246,0.10)] border border-white/[0.07]" style={{ aspectRatio: '16/9' }}>
                <iframe
                  src="https://www.youtube.com/embed/9eqtIyX76oo"
                  title="The Boardroom"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              </div>
              <p className="mt-4 text-[0.75rem] text-slate-600 italic text-center leading-snug">
                The Boardroom — Why are boardrooms run on assumptions, when they could be run on science?
              </p>
            </div>

          </div>
        </div>
      </section>
      {/* section divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-blue-500/[0.22] to-transparent" />

      {/* ── Section 3b: Epistemology Partnership ────────────────── */}
      <section id="epistemology" className="relative py-24 md:py-36 px-6 md:px-10 overflow-hidden">
        <div className="absolute inset-0 bg-white/[0.015]" />

        <div className="relative max-w-7xl mx-auto">

          {/* ── 1. Header: image + text ─────────────────────────── */}
          <div className="flex flex-col md:flex-row gap-14 md:gap-24 items-center mb-20 lg:mb-24">

            {/* Left — Five Rings image */}
            <div className="relative flex-shrink-0 w-52 sm:w-64 mx-auto md:mx-0 md:w-[280px] lg:w-[340px] xl:w-[390px]">
              <div className="pointer-events-none absolute inset-0 rounded-full bg-blue-500/[0.1] blur-[80px] scale-110" />
              <img
                src="/female-five-rings.png"
                alt="Five Rings Methodology"
                className="relative w-full h-auto object-contain drop-shadow-[0_0_60px_rgba(99,102,241,0.25)]"
              />
            </div>

            {/* Right — text */}
            <div className="flex-1 min-w-0">
              <img src="/logo-white-transparent.png" alt="Epistemology" className="h-10 w-auto opacity-80 mb-8" />
              <div className="flex items-center gap-4 mb-5">
                <div className="w-10 h-px bg-blue-500/60 flex-shrink-0" />
                <p className="text-blue-400 font-semibold text-xs uppercase tracking-[0.28em]">Technology Partner • Epistemology</p>
              </div>
              <h2
                className="font-extrabold leading-[1.08] mb-7"
                style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)' }}
              >
                <span className="text-white">Building a causal </span>
                <span className="gradient-text-blue">knowledge base</span>
                <span className="text-white"> of the world</span>
              </h2>

              {/* Authority stat */}
              <div className="flex items-center gap-6 mb-7">
                <div className="text-center flex-shrink-0">
                  <p className="font-extrabold text-white leading-none" style={{ fontSize: 'clamp(2.2rem, 3.5vw, 3rem)' }}>11</p>
                  <p className="text-[0.65rem] text-slate-600 uppercase tracking-[0.2em] mt-1">Years</p>
                </div>
                <div className="w-px h-14 bg-white/[0.08] flex-shrink-0" />
                <p className="text-slate-400 leading-[1.7]" style={{ fontSize: 'clamp(0.9rem, 1.4vw, 1rem)' }}>
                  Epistemology has spent eleven years pioneering structured causal AI and delivers the most advanced and rigorous methodology in the field to tackle the most difficult problems of cause-and-effect.
                </p>
              </div>

              <p className="text-slate-500 leading-[1.8] pt-5 border-t border-white/[0.06]" style={{ fontSize: 'clamp(0.88rem, 1.3vw, 0.97rem)' }}>
                Nordic Numbers deploys <span className="text-blue-400/80">SKAI</span> — Scientific Knowledge through Artificial Intelligence — Epistemology's causal AI platform. A living digital twin of any system, operating in the <em>space of plausibility</em> and continuously generating, testing, and refining hypotheses using the Five Rings methodology.
              </p>
            </div>

          </div>

          {/* ── 2. Track record: 4 sector cards ─────────────────── */}
          <div className="mb-20 lg:mb-24">
            <div className="flex items-center justify-center gap-4 mb-10">
              <div className="flex-1 h-px bg-white/[0.05]" />
              <p className="text-[0.67rem] font-bold uppercase tracking-[0.28em] text-slate-600 flex-shrink-0">Proven across domains</p>
              <div className="flex-1 h-px bg-white/[0.05]" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
              {[
                {
                  accentCls: 'text-blue-400',
                  borderBase: 'border-blue-500/[0.15]',
                  hoverCls: 'hover:border-blue-500/40 hover:shadow-[0_0_40px_rgba(59,130,246,0.08)]',
                  dotCls: 'bg-blue-400',
                  label: 'Investment',
                  headline: 'Causal factor investing for the world\'s largest asset managers.',
                  body: 'Five Rings and SKAI construct portfolios based on the structural causes of return, and not just historical correlations. Framework aligned with the methodology of leading sovereign wealth institutions.',
                  footer: 'Abu Dhabi Investment Authority',
                },
                {
                  accentCls: 'text-indigo-400',
                  borderBase: 'border-indigo-500/[0.15]',
                  hoverCls: 'hover:border-indigo-500/40 hover:shadow-[0_0_40px_rgba(99,102,241,0.08)]',
                  dotCls: 'bg-indigo-400',
                  label: 'Digital Governance',
                  headline: 'Governance frameworks that are transparent, rigorous, and ethically coherent.',
                  body: 'As AI systems take on consequential decisions, explainability and accountability are critical. Designed and applied at the highest levels of global policy.',
                  footer: 'UN · WEF · CSIS · CRI',
                },
                {
                  accentCls: 'text-emerald-400',
                  borderBase: 'border-emerald-500/[0.15]',
                  hoverCls: 'hover:border-emerald-500/40 hover:shadow-[0_0_40px_rgba(16,185,129,0.08)]',
                  dotCls: 'bg-emerald-400',
                  label: 'Climate',
                  headline: 'Causal ecosystem models handling complex environmental feedback loops.',
                  body: 'Applied across bushfire governance, biodiversity planning, and climate risk modelling. Collaborating with leading ecologists, scientific institutions, and government bodies.',
                  footer: 'AU & EU scientific institutions',
                },
                {
                  accentCls: 'text-violet-400',
                  borderBase: 'border-violet-500/[0.15]',
                  hoverCls: 'hover:border-violet-500/40 hover:shadow-[0_0_40px_rgba(139,92,246,0.08)]',
                  dotCls: 'bg-violet-400',
                  label: 'Space',
                  headline: 'Causal reasoning for orbital debris and sovereign actor intent modelling.',
                  body: 'Modelling state actor behaviour, inferring intent from orbital patterns, and quantifying collision risk pathways and mitigation strategies for contested environments.',
                  footer: 'ESA · Australian Space Agency',
                },
              ].map(({ accentCls, borderBase, hoverCls, dotCls, label, headline, body, footer }) => (
                <div
                  key={label}
                  className={`group flex flex-col border ${borderBase} rounded-2xl p-6 bg-white/[0.01] transition-all duration-500 ${hoverCls}`}
                >
                  <p className={`text-[0.67rem] font-bold uppercase tracking-[0.25em] ${accentCls} mb-3`}>{label}</p>
                  <p className="text-white font-semibold leading-snug mb-3" style={{ fontSize: 'clamp(0.88rem, 1.3vw, 0.97rem)' }}>{headline}</p>
                  <p className="text-slate-500 leading-[1.75] flex-1" style={{ fontSize: 'clamp(0.82rem, 1.1vw, 0.88rem)' }}>{body}</p>
                  <div className="mt-4 pt-4 border-t border-white/[0.06] flex items-center gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotCls} opacity-60`} />
                    <p className={`text-[0.67rem] font-semibold ${accentCls} opacity-60`}>{footer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── 3. Five Rings Methodology ─────────────────────────── */}
          <div className="mb-20 lg:mb-24">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-4 mb-5">
                <div className="w-8 h-px bg-blue-500/60" />
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-blue-400">The Five Rings Methodology</p>
                <div className="w-8 h-px bg-blue-500/60" />
              </div>
              <h3
                className="font-extrabold text-white leading-[1.1] mb-4"
                style={{ fontSize: 'clamp(1.5rem, 2.8vw, 2.2rem)' }}
              >
                An Algorithm for Intelligent Action
              </h3>
              <p className="text-slate-500 max-w-lg mx-auto leading-[1.7]" style={{ fontSize: 'clamp(0.88rem, 1.3vw, 0.97rem)' }}>
                A rigorous framework for computing the optimal next-best-action given your goals, knowledge, evidence, and remaining uncertainties.
              </p>
            </div>

            <div className="relative">
              {/* Connecting line on large screens */}
              <div className="hidden lg:block absolute top-[1.35rem] left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-4">
                {[
                  { n: '01', name: 'Axiology',    q: 'What do we value?',          desc: 'Formalises goals, objectives, and value functions. Without clarity on what we are optimising for, no strategy can be coherent.' },
                  { n: '02', name: 'Ontology',    q: 'What do we believe?',        desc: 'Constructs the causal model of the system, the entities, relationships, and mechanisms we believe govern its behaviour.' },
                  { n: '03', name: 'Semiology',   q: 'What does our data mean?',   desc: 'Translates evidence into meaning. Bridges the gap between raw signals and the interpretations that inform decisions.' },
                  { n: '04', name: 'Epistemology',q: 'What do we know?',           desc: 'Beliefs are tested against evidence through a systematic approach that quantifies our knowledge and remaining uncertainty.' },
                  { n: '05', name: 'Strategy',    q: 'What should we do next?',    desc: 'Computes the optimal next-best-action given goals, the validated causal model, and remaining uncertainty. Intelligence becomes action.' },
                ].map(({ n, name, q, desc }) => (
                  <div key={n} className="flex flex-col items-center text-center px-2">
                    <div className="relative z-10 w-[2.75rem] h-[2.75rem] rounded-full border border-blue-500/30 bg-[#060A0F] flex items-center justify-center mb-4 flex-shrink-0">
                      <span className="text-[0.68rem] font-bold text-blue-400 tracking-wider">{n}</span>
                    </div>
                    <p className="font-bold text-white mb-1" style={{ fontSize: 'clamp(0.88rem, 1.2vw, 0.97rem)' }}>{name}</p>
                    <p className="text-blue-400/60 text-[0.73rem] italic mb-3 leading-snug">{q}</p>
                    <p className="text-slate-600 leading-[1.65] text-[0.8rem]">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── 4. Validation quote ──────────────────────────────── */}
          <div className="relative border border-blue-500/20 rounded-2xl px-8 py-10 md:px-14 md:py-12 bg-blue-500/[0.03] text-center shadow-[0_0_60px_rgba(59,130,246,0.06)]">
            <div className="absolute -top-[0.85rem] left-1/2 -translate-x-1/2 px-5 bg-[#060A0F]">
              <p className="text-[0.63rem] font-bold uppercase tracking-[0.28em] text-blue-400/70">Independent Validation</p>
            </div>
            <svg className="w-8 h-8 text-blue-400/40 mx-auto mb-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.293-3.995 5.847h3v10h-8.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.293-3.996 5.847h3.001v10h-9.001z" />
            </svg>
            <blockquote
              className="text-white font-semibold leading-[1.65] mb-8 max-w-2xl mx-auto"
              style={{ fontSize: 'clamp(1.05rem, 1.9vw, 1.35rem)' }}
            >
              "An order of magnitude improvement relative to competing best practice approaches."
            </blockquote>
            <div className="flex flex-col items-center gap-1.5">
              <p className="text-white font-semibold text-sm">Professor Hugh Possingham <span className="text-slate-400 font-normal">AO · FAA · FTSE</span></p>
              <p className="text-slate-400 text-xs tracking-wide">Chief Scientist of Queensland</p>
              <p className="text-slate-500 text-[0.72rem] mt-1">Independent evaluation of the Five Rings Methodology against competing best-practice approaches</p>
            </div>
          </div>

        </div>
      </section>
      {/* section divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-blue-500/[0.22] to-transparent" />

      {/* ── Section 4 + 7: Hypothesis-driven ──────────────────── */}
      <section className="relative py-28 md:py-36 px-6 md:px-10 overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto">

          {/* Heading — full-width centered */}
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-3 mb-5">
              <div className="w-8 h-px bg-blue-500/60" />
              <p className="text-blue-400 font-semibold text-xs uppercase tracking-[0.28em]">Go faster with scientific precision</p>
              <div className="w-8 h-px bg-blue-500/60" />
            </div>
            <h2
              className="font-extrabold leading-[1.07]"
              style={{ fontSize: 'clamp(2.2rem, 4.2vw, 3.4rem)' }}
            >
              <span className="text-white">The future belongs to </span>
              <span className="gradient-text-blue">hypothesis-driven</span>
              <span className="text-white"> organisations</span>
            </h2>
          </div>

          {/* Bento grid */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_370px] gap-5 mb-10">

            {/* Left: two stacked content cards */}
            <div className="flex flex-col gap-5">

              {/* Prose card */}
              <div className="flex-1 border border-white/[0.07] rounded-2xl p-7 bg-white/[0.02]">
                <p className="text-[0.6rem] font-bold uppercase tracking-[0.28em] text-slate-600 mb-4">The challenge</p>
                <p className="text-slate-400 leading-[1.85]" style={{ fontSize: 'clamp(0.96rem, 1.35vw, 1.03rem)' }}>
                  Business and institutional challenges grow more complex every year. Simple tools will not solve them.
                  You need to trace the <strong className="text-slate-200 font-semibold">causes behind outcomes</strong>,
                  not the correlations that describe them. Nordic Numbers creates knowledge you can act on.
                </p>
                <div className="h-px bg-white/[0.06] my-5" />
                <p className="text-[0.6rem] font-bold uppercase tracking-[0.28em] text-slate-600 mb-4">Our approach</p>
                <p className="text-slate-400 leading-[1.85]" style={{ fontSize: 'clamp(0.96rem, 1.35vw, 1.03rem)' }}>
                  Our methodology encodes the mechanisms behind outcomes, generalises across contexts,
                  works in data-sparse environments, enables agent intent modelling, and allows
                  next-best-action calculations in the face of uncertainty.
                </p>
              </div>

              {/* Key insight card */}
              <div className="relative border border-blue-500/[0.18] rounded-2xl p-6 bg-blue-500/[0.04]">
                <div className="absolute top-0 left-6 -translate-y-1/2 px-3 bg-[#060A0F]">
                  <span className="text-[0.6rem] font-bold uppercase tracking-[0.28em] text-blue-400/70">Key insight</span>
                </div>
                <p className="text-slate-300 leading-[1.78]" style={{ fontSize: 'clamp(0.94rem, 1.3vw, 1.01rem)' }}>
                  Scientific reasoning reveals how systems actually work, what levers change their
                  behaviour, and how causes and effects are related. We bring this precision to business and strategy.
                </p>
              </div>

            </div>

            {/* Right: atom + pills — single tall card */}
            <div className="border border-white/[0.07] rounded-2xl bg-white/[0.02] flex flex-col overflow-hidden">

              {/* Atom */}
              <div className="flex-1 relative flex items-center justify-center p-8 min-h-[260px]">
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden>
                  <div className="w-[280px] h-[280px] rounded-full bg-blue-600/[0.12] blur-[70px]" />
                </div>
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden>
                  <div className="w-[160px] h-[160px] rounded-full bg-indigo-500/[0.09] blur-[40px]" />
                </div>
                <div className="relative w-[230px] h-[230px] lg:w-[250px] lg:h-[250px]">
                  <AtomOrbit />
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-white/[0.06] mx-6" />

              {/* Pills in 2-col grid */}
              <div className="p-5 grid grid-cols-2 gap-2">
                {[
                  { label: 'Causal Reasoning',     cls: 'border-blue-500/20 bg-blue-500/[0.07] text-blue-300' },
                  { label: 'System Reasoning', cls: 'border-blue-500/20 bg-blue-500/[0.07] text-blue-300' },
                  { label: 'Decisive Action',      cls: 'border-blue-500/20 bg-blue-500/[0.07] text-blue-300' },
                  { label: 'Beyond Correlation',   cls: 'border-indigo-500/20 bg-indigo-500/[0.07] text-indigo-300' },
                  { label: 'Data-Sparse Ready',    cls: 'border-indigo-500/20 bg-indigo-500/[0.07] text-indigo-300' },
                  { label: 'Mechanism-Based',      cls: 'border-indigo-500/20 bg-indigo-500/[0.07] text-indigo-300' },
                ].map(({ label, cls }) => (
                  <span key={label} className={`text-[0.66rem] font-semibold px-2.5 py-1.5 rounded-lg border tracking-wide text-center leading-tight ${cls}`}>
                    {label}
                  </span>
                ))}
              </div>

            </div>
          </div>

          {/* CTA */}
          <div className="flex justify-center">
            <Link
              to="/contact"
              className="relative inline-flex items-center gap-3 px-8 py-3.5 rounded-full border border-blue-500/60 text-white font-semibold text-sm tracking-wide group overflow-hidden transition-all duration-500 hover:border-blue-400 hover:shadow-[0_0_36px_rgba(59,130,246,0.28)]"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <span className="relative">Get in touch</span>
              <svg className="relative w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
